
import { supabase } from '@/integrations/supabase/client';
import { InterviewRequestResponse } from './types';

export class DebugRequestService {
  
  static async getRequestByLinkWithFullDebug(requestLink: string): Promise<{
    data: InterviewRequestResponse | null;
    error: string | null;
    debugInfo: any;
  }> {
    const debugInfo: any = {
      timestamp: new Date().toISOString(),
      requestLink: requestLink,
      steps: []
    };

    try {
      console.log('🔍 DEBUG SERVICE: Starting comprehensive debug for link:', requestLink);
      debugInfo.steps.push({ step: 'start', data: { requestLink } });
      
      // Step 1: Raw Supabase query with full debugging
      const queryStart = performance.now();
      const { data: rawData, error: rawError } = await supabase
        .from('interview_requests')
        .select('*')
        .eq('request_link', requestLink.trim());

      const queryTime = performance.now() - queryStart;
      
      debugInfo.steps.push({
        step: 'raw_query',
        queryTime: `${queryTime}ms`,
        rawData: rawData,
        rawError: rawError,
        dataType: typeof rawData,
        dataLength: rawData ? rawData.length : 0
      });

      console.log('🔍 DEBUG SERVICE: Raw database response:', {
        hasData: !!rawData,
        dataLength: rawData ? rawData.length : 0,
        hasError: !!rawError,
        rawData: rawData,
        rawError: rawError
      });

      if (rawError) {
        debugInfo.steps.push({ step: 'error', error: rawError });
        return { data: null, error: 'Database error: ' + rawError.message, debugInfo };
      }

      if (!rawData || rawData.length === 0) {
        debugInfo.steps.push({ step: 'no_data' });
        return { data: null, error: 'Interview request not found', debugInfo };
      }

      const firstRecord = rawData[0];
      
      // Step 2: Detailed question analysis
      const questionAnalysis = {
        exists: 'question' in firstRecord,
        value: firstRecord.question,
        type: typeof firstRecord.question,
        length: firstRecord.question ? firstRecord.question.length : 0,
        isNull: firstRecord.question === null,
        isUndefined: firstRecord.question === undefined,
        isEmptyString: firstRecord.question === '',
        trimmedLength: firstRecord.question ? firstRecord.question.trim().length : 0,
        charCodes: firstRecord.question ? Array.from(firstRecord.question).map(char => char.charCodeAt(0)) : [],
        raw: JSON.stringify(firstRecord.question),
        stringified: String(firstRecord.question),
        // Check for invisible characters
        hasInvisibleChars: firstRecord.question ? /[\u0000-\u001F\u007F-\u009F]/.test(firstRecord.question) : false
      };

      debugInfo.steps.push({
        step: 'question_analysis',
        questionAnalysis: questionAnalysis
      });

      console.log('🔍 DEBUG SERVICE: Detailed question analysis:', questionAnalysis);

      // Step 3: Check for specific problematic patterns
      const problematicPatterns = {
        isNullString: firstRecord.question === 'null',
        isUndefinedString: firstRecord.question === 'undefined',
        isObjectString: firstRecord.question === '[object Object]',
        isEmpty: !firstRecord.question || firstRecord.question.trim().length === 0,
        isPlaceholder: firstRecord.question === '[SPØRSMÅL MANGLER - VENNLIGST KONTAKT SUPPORT]' || 
                      firstRecord.question === '[MISSING QUESTION - CONTACT SUPPORT]',
        hasOnlyWhitespace: firstRecord.question && firstRecord.question.trim() === '' && firstRecord.question.length > 0
      };

      debugInfo.steps.push({
        step: 'pattern_check',
        problematicPatterns: problematicPatterns
      });

      console.log('🔍 DEBUG SERVICE: Problematic patterns check:', problematicPatterns);

      // Step 4: Full record analysis
      const recordAnalysis = {
        id: firstRecord.id,
        created_at: firstRecord.created_at,
        request_link: firstRecord.request_link,
        status: firstRecord.status,
        interviewee_name: firstRecord.interviewee_name,
        interviewee_email: firstRecord.interviewee_email,
        context: firstRecord.context,
        allFields: Object.keys(firstRecord),
        recordSize: JSON.stringify(firstRecord).length
      };

      debugInfo.steps.push({
        step: 'record_analysis',
        recordAnalysis: recordAnalysis
      });

      console.log('🔍 DEBUG SERVICE: Full record analysis:', recordAnalysis);

      // Step 5: Data serialization test
      let serializationTest;
      try {
        const serialized = JSON.stringify(firstRecord);
        const parsed = JSON.parse(serialized);
        serializationTest = {
          canSerialize: true,
          serializedLength: serialized.length,
          parsedQuestionMatches: parsed.question === firstRecord.question,
          parsedQuestion: parsed.question
        };
      } catch (e) {
        serializationTest = {
          canSerialize: false,
          error: e instanceof Error ? e.message : String(e)
        };
      }

      debugInfo.steps.push({
        step: 'serialization_test',
        serializationTest: serializationTest
      });

      console.log('🔍 DEBUG SERVICE: Serialization test:', serializationTest);

      return { 
        data: firstRecord as InterviewRequestResponse, 
        error: null, 
        debugInfo 
      };

    } catch (exception) {
      const exceptionInfo = {
        message: exception instanceof Error ? exception.message : String(exception),
        stack: exception instanceof Error ? exception.stack : undefined,
        name: exception instanceof Error ? exception.name : 'Unknown'
      };
      
      debugInfo.steps.push({
        step: 'exception',
        exceptionInfo: exceptionInfo
      });
      
      console.error('💥 DEBUG SERVICE: Exception:', exception);
      return { 
        data: null, 
        error: exceptionInfo.message, 
        debugInfo 
      };
    }
  }
}
